import { Fragment, useMemo } from 'react';
import { set as localStorageSet } from 'local-storage';
import PageLoader from 'src/components/PageLoader/PageLoader';
import PageNotFound from 'src/components/PageNotFound/PageNotFound';
import ExpiredJob from 'src/components/ExpiredJob/ExpiredJob';
import { SafetyHelmet } from 'src/components/SafetyHelmet/SafetyHelmet';
import { v4 as uuid } from 'uuid';
import {
  clearJobDetails,
  fetchUnifiedJob,
  fetchUnifiedJobPersonalised,
  type JobDetails as JobDetailsType,
  jobDetailsPageLoaded,
} from 'src/store/jobdetails/jobdetails';
import { getJobDetailsHead } from '@seek/seek-jobs-seo';
// @ts-expect-error: non-ts file
import { provideHooks } from 'redial';
import { useSelector } from 'src/store/react';
import {
  selectAuthenticated,
  selectCountry,
  selectFeatureFlag,
  selectIsJobDetailsPending,
  selectJobDetailsResult,
  selectLanguage,
  selectLocation,
  selectSessionId,
  selectUserTestHeaders,
  selectZone,
} from 'src/store/selectors';
import type { RedialLocals } from 'src/types/RedialLocals';
import { pageLoaded } from 'src/store/ui/ui';
import {
  createGetSolMetadataForJDP,
  trackJobDetailsLoaded,
} from '../hooks/trackJobDetailsLoaded';
import {
  isBrowserAnalyticsFacade,
  useAnalyticsFacade,
} from '../modules/AnalyticsFacade';
import { jobOrExpiredJobRegex } from 'src/modules/routes-regexp';
import { useQuery } from '@apollo/client';
import {
  GET_JOB_DETAILS_PERSONALISED,
  GET_JOB_DETAILS_WITH_PERSONALISED,
} from 'src/modules/graphql/queries/jobDetails';
import type { GetJobDetailsVariables } from 'src/modules/graphql/queries/types/GetJobDetails';
import { logErrorToRaygun } from 'src/modules/raygun-logger';
import { CREATE_SAVED_JOB } from 'src/modules/graphql/mutations/createSavedJob';
import type { CreateSavedJob } from 'src/modules/graphql/mutations/types/CreateSavedJob';
import { SEARCH_SAVED_AND_APPLIED_JOBS } from 'src/modules/graphql/queries';
import type {
  GetJobDetailsPersonalised,
  SearchSavedAndAppliedJobs,
} from 'src/modules/graphql/queries/types';
import { logger } from 'src/modules/logger';
import { useSetHubbleTags } from '../modules/hubble';
import { useSelectedLocale } from 'src/hooks/useSelectedLocale';
import loadable from 'sku/@loadable/component';
import {
  getJobActionOrigin,
  getViewJobOriginRef,
} from 'src/utils/eventCapture/eventCaptureUtils';
import { getBrandFromZone } from 'src/config/utils/getBrandFromZone';
import { getTimezone } from 'src/hooks/useTimezone';

export const JobDetailsPageView = loadable(
  /* #__LOADABLE__ */ () =>
    import(
      /* webpackChunkName: "JobDetailsPage" */ 'src/components/JobDetailsPage/JobDetailsPage'
    ),
  {
    resolveComponent: (m) => m.default,
  },
);

interface GetJobDetailsApollo {
  jobDetails: JobDetailsType;
}

const extractIdFromPath = (path?: string) => {
  if (!path) {
    return '';
  }
  const match = path.match(jobOrExpiredJobRegex);
  return match ? match[2] : '';
};

let jobDetailsViewedCorrelationId: string;
const getJDVCorrelationId = (): void => {
  jobDetailsViewedCorrelationId = uuid();
};

const hooks = {
  first: () => {
    getJDVCorrelationId();
  },
  fetch: ({
    analyticsFacade,
    apolloClient,
    dispatch,
    getState,
    path,
    zone,
    cookies,
    languageCode,
    locale,
    country,
  }: RedialLocals) => {
    const jobId = extractIdFromPath(path);
    const existingJobId = getState().jobdetails.result?.job?.id;
    const isApolloJDVEnabled = cookies.ENABLE_APOLLO_JDV;

    if (existingJobId && jobId !== String(existingJobId)) {
      dispatch(clearJobDetails());
    }

    return isApolloJDVEnabled
      ? null
      : dispatch(
          fetchUnifiedJob({
            analyticsFacade,
            apolloClient,
            jobId,
            jobDetailsViewedCorrelationId,
            jobseekerSessionId: cookies.JobseekerSessionId,
            zone,
            locale,
            languageCode,
            countryCode: country,
          }),
        );
  },
  defer: ({
    apolloClient,
    authenticated,
    dispatch,
    path,
    cookies,
    languageCode,
    locale,
    zone,
    getState,
  }: RedialLocals) => {
    const jobId = extractIdFromPath(path);
    localStorageSet('lastJobId', jobId);
    const salaryTransparencyEnabled =
      getState().featureFlags.salaryTransparency;

    const isApolloJDVEnabled = cookies.ENABLE_APOLLO_JDV;

    if (authenticated && !isApolloJDVEnabled) {
      return dispatch(
        fetchUnifiedJobPersonalised({
          apolloClient,
          jobId,
          jobseekerSessionId: cookies.JobseekerSessionId,
          jobDetailsViewedCorrelationId,
          languageCode,
          locale,
          zone,
          salaryTransparencyEnabled,
        }),
      );
    }
    return;
  },

  authenticated: ({
    analyticsFacade,
    apolloClient,
    getState,
    routeEnter,
    handleIntent,
    zone,
    cookies,
    languageCode,
    locale,
    country,
  }: RedialLocals) => {
    if (routeEnter && handleIntent) {
      handleIntent<string>('savejob', (jobId) => {
        const isApolloJDVEnabled = cookies.ENABLE_APOLLO_JDV;
        let jobDetails: JobDetailsType | null;

        const {
          user: { sessionId },
          results: { jobIds },
          featureFlags: { salaryTransparency },
        } = getState();

        if (!sessionId) {
          logErrorToRaygun({
            error: new Error(
              'Save job event capture is not sent: session id is missing (JDP)',
            ),
            customData: { cookie: document.cookie },
          });
        }

        return apolloClient
          .mutate<CreateSavedJob>({
            mutation: CREATE_SAVED_JOB,
            variables: {
              input: {
                id: jobId,
                zone,
                eventCaptureData: {
                  channel: 'web',
                  view: 'jobDetails',
                  serpTracking: '',
                  eventCaptureSessionId: sessionId,
                },
              },
              locale,
            },
            update: (cache) => {
              const searchSavedAndAppliedJobsCachedData =
                cache.readQuery<SearchSavedAndAppliedJobs>({
                  query: SEARCH_SAVED_AND_APPLIED_JOBS,
                  variables: { jobIds },
                });

              if (searchSavedAndAppliedJobsCachedData) {
                cache.writeQuery({
                  query: SEARCH_SAVED_AND_APPLIED_JOBS,
                  variables: { jobIds },
                  data: {
                    viewer: {
                      __typename: 'Candidate',
                      searchAppliedJobs:
                        searchSavedAndAppliedJobsCachedData?.viewer
                          ?.searchAppliedJobs,
                      searchSavedJobs: [
                        ...(searchSavedAndAppliedJobsCachedData?.viewer
                          ?.searchSavedJobs ?? []),
                        {
                          __typename: 'SearchSavedJobPartial',
                          jobId,
                        },
                      ],
                    },
                  },
                });
              }

              const jobDetailsPersonalisedCachedData =
                cache.readQuery<GetJobDetailsPersonalised>({
                  query: GET_JOB_DETAILS_PERSONALISED,
                  variables: {
                    id: jobId,
                    languageCode,
                    locale,
                    zone,
                    salaryTransparencyEnabled: salaryTransparency,
                  },
                });

              if (jobDetailsPersonalisedCachedData) {
                cache.writeQuery({
                  query: GET_JOB_DETAILS_PERSONALISED,
                  variables: {
                    id: jobId,
                    languageCode,
                    locale,
                    zone,
                    salaryTransparencyEnabled: salaryTransparency,
                  },
                  data: {
                    jobDetails: {
                      personalised: {
                        ...jobDetailsPersonalisedCachedData?.jobDetails
                          ?.personalised,
                        isSaved: true,
                      },
                      __typename: 'JobDetails',
                    },
                  },
                });
              }
            },
          })
          .then(() => {
            const state = getState();

            (async () => {
              if (isApolloJDVEnabled) {
                const jobDetailsApollo = await apolloClient.query<
                  GetJobDetailsApollo,
                  GetJobDetailsVariables
                >({
                  query: GET_JOB_DETAILS_WITH_PERSONALISED,
                  variables: {
                    jobId,
                    jobDetailsViewedCorrelationId,
                    sessionId: cookies.JobseekerSessionId,
                    zone,
                    locale,
                    languageCode,
                    countryCode: country,
                    timezone: getTimezone(),
                    salaryTransparencyEnabled: salaryTransparency,
                  },
                });
                jobDetails = jobDetailsApollo?.data?.jobDetails;
              } else {
                jobDetails = state.jobdetails.result;
              }
            })();

            if (jobDetails && isBrowserAnalyticsFacade(analyticsFacade)) {
              analyticsFacade.saveJobCreated(
                {
                  solMetadata: createGetSolMetadataForJDP({
                    experiments: state.experiments,
                    hash: state.location.hash,
                    jobId: jobDetails?.job?.id,
                    query: state.location.query,
                  })(),
                },
                {
                  jobId: jobDetails?.job?.id,
                  linkPosition: 'job details',
                },
              );
            }
          });
      });

      return;
    }

    return false;
  },
  pageLoad: ({
    analyticsFacade,
    dispatch,
    getState,
    query,
    apolloClient,
    path,
    locale,
    zone,
    cookies,
    languageCode,
    country,
  }: RedialLocals) => {
    const state = getState();
    const id = extractIdFromPath(path);
    let jobDetails: JobDetailsType | null;
    const currentPath = state.location.pathname;
    const prevPath = state.location.prevPathname;
    const salaryTransparencyEnabled = state.featureFlags.salaryTransparency;

    const isApolloJDVEnabled = cookies.ENABLE_APOLLO_JDV;

    (async () => {
      if (isApolloJDVEnabled) {
        const jobDetailsApollo = await apolloClient.query<
          GetJobDetailsApollo,
          GetJobDetailsVariables
        >({
          query: GET_JOB_DETAILS_WITH_PERSONALISED,
          variables: {
            jobId: id,
            jobDetailsViewedCorrelationId,
            sessionId: cookies.JobseekerSessionId,
            zone,
            locale,
            languageCode,
            countryCode: country,
            timezone: getTimezone(),
            salaryTransparencyEnabled,
          },
        });
        jobDetails = jobDetailsApollo?.data?.jobDetails;
      } else {
        jobDetails = state.jobdetails.result;
      }

      const jobListingType = jobDetails?.job?.tracking?.adProductType as
        | string
        | undefined;

      const getSolMetadata = createGetSolMetadataForJDP({
        experiments: state.experiments,
        hash: state.location.hash,
        jobId: id,
        query,
      });

      /**
       * When the current pathname === previous pathname it a signal that the user has
       * accessed this job via an external source.
       */
      const jobListingPosition =
        currentPath === prevPath ? -1 : getSolMetadata().sectionRank;

      const ref = getViewJobOriginRef();
      const origin = getJobActionOrigin();

      trackJobDetailsLoaded(
        analyticsFacade,
        {
          correlationId: jobDetailsViewedCorrelationId,
          getSolMetadata,
          jobDetails,
          jobListingPosition,
          jobListingType,
          jobViewType: 'standalone',
          omnitureTracking: query?.tracking,
          jobViewOrigin: ref,
          jobActionOrigin: origin,
        },
        state,
        apolloClient,
      );
    })();

    dispatch(jobDetailsPageLoaded(Boolean(query?.send)));

    return dispatch(pageLoaded(true));
  },
};

const ExpiredJobWithSafetyHelmet = (
  helmetProps: ReturnType<typeof getJobDetailsHead>,
) => (
  <Fragment>
    <SafetyHelmet {...helmetProps} />
    <ExpiredJob />
  </Fragment>
);

const JobDetails = () => {
  const location = useSelector(selectLocation);
  const jobDetails = useSelector(selectJobDetailsResult);
  const jobPending = useSelector(selectIsJobDetailsPending);
  const testHeaders = useSelector(selectUserTestHeaders);
  const zone = useSelector(selectZone);
  const brand = getBrandFromZone(zone);
  useSetHubbleTags(testHeaders);

  const jobIsExpired = useMemo(() => {
    if (jobDetails?.job && jobDetails?.job?.status) {
      return (
        jobDetails?.job?.status !== 'Active' &&
        location.pathname.search('expiredjob') < 0
      );
    }
    return false;
  }, [jobDetails, location]);

  const helmetProps = useMemo(() => {
    if (jobDetails) {
      return getJobDetailsHead({
        abstract: jobDetails?.job?.abstract,
        location: jobDetails?.job?.location?.label,
        title: jobDetails?.job?.title,
        brand,
      });
    }
    return {};
  }, [jobDetails, brand]);

  if (!jobDetails) {
    return jobPending ? <PageLoader /> : <PageNotFound />;
  }

  if (jobIsExpired) {
    return <ExpiredJobWithSafetyHelmet {...helmetProps} />;
  }

  return (
    <Fragment>
      <SafetyHelmet {...helmetProps} />
      {jobDetails && <JobDetailsPageView jobDetails={jobDetails} />}
    </Fragment>
  );
};

const JobDetailsGraph = () => {
  const location = useSelector(selectLocation);
  const jobId = location?.pathname && extractIdFromPath(location?.pathname);
  const locale = useSelectedLocale();
  const zone = useSelector(selectZone);
  const jobseekerSessionId = useSelector(selectSessionId);
  const isAuthenticated = useSelector(selectAuthenticated);
  const analyticsFacade = useAnalyticsFacade();
  const languageCode = useSelector(selectLanguage);
  const countryCode = useSelector(selectCountry);
  const salaryTransparencyEnabled = useSelector(
    selectFeatureFlag('salaryTransparency'),
  );

  const {
    data,
    loading: jobPending,
    error,
  } = useQuery(GET_JOB_DETAILS_WITH_PERSONALISED, {
    variables: {
      jobId,
      jobDetailsViewedCorrelationId: jobDetailsViewedCorrelationId || '',
      sessionId: jobseekerSessionId || '',
      zone,
      locale,
      languageCode,
      countryCode,
      timezone: getTimezone(),
      salaryTransparencyEnabled,
    },
    ...(isAuthenticated && { fetchPolicy: 'cache-and-network' }),
    errorPolicy: 'all',
  });

  if (error) {
    logger.error({ err: error }, 'JobDetails GraphQL Error');
    logErrorToRaygun({
      error: new Error('JobDetails GraphQL Error'),
      customData: { error },
    });
  }

  const jobDetails = data?.jobDetails;

  if (jobDetails) {
    analyticsFacade.jobDetailsFetched(jobDetails);
  }

  const jobIsExpired = useMemo(() => {
    if (jobDetails?.job && jobDetails?.job?.status) {
      return (
        jobDetails?.job?.status !== 'Active' &&
        location.pathname.search('expiredjob') < 0
      );
    }
    return false;
  }, [jobDetails, location]);

  const helmetProps = useMemo(() => {
    if (jobDetails) {
      return getJobDetailsHead({
        abstract: jobDetails?.job?.abstract,
        location: jobDetails?.job?.location?.label,
        title: jobDetails?.job?.title,
      });
    }
    return {};
  }, [jobDetails]);

  if (!jobDetails) {
    return jobPending ? <PageLoader /> : <PageNotFound />;
  }

  if (jobIsExpired) {
    return <ExpiredJobWithSafetyHelmet {...helmetProps} />;
  }

  return (
    <Fragment>
      <SafetyHelmet {...helmetProps} />
      {jobDetails && <JobDetailsPageView jobDetails={jobDetails} />}
    </Fragment>
  );
};

const _JobDetails = () => {
  const isApolloJDVEnabled = useSelector(
    selectFeatureFlag('isApolloJDVEnabled'),
  );

  return isApolloJDVEnabled ? <JobDetailsGraph /> : <JobDetails />;
};

export default provideHooks(hooks)(_JobDetails);
